import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { TIER_LIMITS, type PlanTier, type TierLimits, tierAtLeast } from "@/lib/tiers";
import { getStripeEnvironment } from "@/lib/stripe";
import { usePlatformSettings } from "./usePlatformSettings";

interface SubscriberRow {
  tier: PlanTier;
  status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  billing_interval: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  grandfathered: boolean;
}

interface SubscriptionState {
  tier: PlanTier;
  limits: TierLimits;
  status: string;
  isGrandfathered: boolean;
  hasActiveBilling: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  billingInterval: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  hasFeature: (required: PlanTier) => boolean;
}

export function useSubscription(): SubscriptionState {
  const { user, loading: authLoading } = useAuth();
  const { pricingEnabled, loading: settingsLoading } = usePlatformSettings();
  const [row, setRow] = useState<SubscriberRow | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setRow(null);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("subscribers")
      .select("tier, status, stripe_customer_id, stripe_subscription_id, stripe_price_id, billing_interval, current_period_end, cancel_at_period_end, grandfathered")
      .eq("user_id", user.id)
      .maybeSingle();
    if (error) console.error("useSubscription load error:", error);
    setRow((data as SubscriberRow) ?? null);
    setLoading(false);
  }, [user]);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    // Ask the server to verify with Stripe and write back to the table
    try {
      await supabase.functions.invoke("check-subscription", {
        body: { environment: getStripeEnvironment() },
      });
    } catch (e) {
      console.error("check-subscription failed:", e);
    }
    await load();
  }, [user, load]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  // Realtime updates when webhook writes changes
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscribers:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscribers", filter: `user_id=eq.${user.id}` },
        () => { load(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, load]);

  const tier: PlanTier = row?.tier ?? "personal";
  const isActive = row?.status === "active" || row?.status === "trialing" || row?.status === "grandfathered" ||
    (row?.status === "canceled" && row?.current_period_end && new Date(row.current_period_end) > new Date());
  // When pricing is globally disabled (pre-launch beta), everyone gets full access.
  const effectiveTier: PlanTier = !pricingEnabled
    ? "organization"
    : isActive
      ? tier
      : "personal";

  return {
    tier: effectiveTier,
    limits: TIER_LIMITS[effectiveTier],
    status: row?.status ?? "inactive",
    isGrandfathered: !!row?.grandfathered,
    hasActiveBilling: !!row?.stripe_subscription_id,
    cancelAtPeriodEnd: !!row?.cancel_at_period_end,
    currentPeriodEnd: row?.current_period_end ?? null,
    billingInterval: row?.billing_interval ?? null,
    loading: loading || authLoading || settingsLoading,
    refresh,
    hasFeature: (required: PlanTier) => !pricingEnabled || tierAtLeast(effectiveTier, required),
  };
}
